import { signalStore, withState, withMethods, withHooks, withComputed, patchState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { inject, computed, effect, Injector, runInInjectionContext, DestroyRef } from '@angular/core';
import { LoggerService } from '../core/services/logger.service';
import { PermissionApiService } from '../core/api/services/permission-api.service';
import { AuthStore } from './auth.store';
import { withRequestStatus } from './features/with-request-status';
import { switchMap, tap, catchError, map, of, type Observable } from 'rxjs';
import { User } from '../core/models/user.model';
import type { Permission, Role, UserRole, EffectivePermissions } from '@core/models/permission.model';
import {
  type PermissionResult,
  type PermissionLoadError,
  classifyPermissionError,
} from './permission-result.types';
import { PermissionWebSocketService } from '../features/permissions-management/services/permission-websocket.service';

export type { PermissionResult, PermissionLoadError } from './permission-result.types';
export { classifyPermissionError, isPermissionResultOk } from './permission-result.types';

export type { Permission, Role, UserRole, EffectivePermissions } from '@core/models/permission.model';

export interface UserPermissions {
  userId: string;
  permissions: Permission[];
  roles: Role[];
  effectivePermissions: string[];
  tenantPermissions: Record<string, Permission[]>;
  lastUpdated: Date;
}

export interface PermissionCheck {
  permission: string;
  resource?: string;
  tenant_id?: string;
  context?: Record<string, unknown>;
}

// Context used when evaluating permissions. Explicitly type common fields to
// satisfy `noPropertyAccessFromIndexSignature` while still allowing extras.
type PermissionContext = { resource?: string; action?: string } & Record<string, unknown>;

/**
 * Enhanced PermissionStore with full RBAC capabilities
 * 
 * Features:
 * - Role-based access control
 * - Permission hierarchy and inheritance
 * - Tenant-scoped permissions
 * - Real-time permission checking
 * - Permission caching and optimization
 * - Audit trail for permission changes
 */
export const PermissionStore = signalStore(
  { providedIn: 'root' },
  
  // State definition
  withState({
    // Core permission data
    permissions: [] as Permission[],
    roles: [] as Role[],
    userRoles: [] as UserRole[],
    effectivePermissions: null as EffectivePermissions | null,

    // Error tracking for load operations — typed per GAP-FE-026 so guards
    // / banner UX can switch on the discriminated `kind` instead of
    // matching exception messages.
    permissionLoadError: null as PermissionLoadError | null,
    roleLoadError: null as PermissionLoadError | null,
    userRoleLoadError: null as PermissionLoadError | null,
    effectivePermissionsLoadError: null as PermissionLoadError | null,

    // Permission matrix data
    allUsers: [] as User[],
    permissionMatrix: new Map<string, Set<string>>(),

    // Caching
    permissionCache: new Map<string, boolean>(),
    cacheExpiry: null as Date | null,

    // UI state
    selectedUserId: null as string | null,
    selectedRoleId: null as string | null,
    permissionFilter: '',
    roleFilter: '',

    // Real-time updates
    lastUpdated: null as Date | null,
    websocketConnected: false
  }),
  
  // Add request status
  withRequestStatus(),
  
  // Computed signals
  withComputed((store) => {
    const authStore = inject(AuthStore);
    // Define related computeds first, then reference the local vars to avoid
    // typing issues when accessing other computeds during construction.
    const currentUserRoles = computed(() => {
      const currentUser = authStore.currentUser();
      if (!currentUser) {return [] as UserRole[];}
      const now = Date.now();

      return store.userRoles().filter((ur: UserRole) => {
        if (ur.user_id !== currentUser.id) {return false;}
        if (ur.expires_at) {
          const expiresTs = new Date(ur.expires_at).getTime();
          if (!isNaN(expiresTs) && expiresTs < now) {return false;}
        }
        return true;
      });
    });

    const currentUserPermissions = computed(() => {
      const roles = currentUserRoles();
      const permissions = new Map<string, Permission>();

      roles.forEach((userRole: UserRole) => {
        userRole.role.permissions.forEach((permission: Permission) => {
          permissions.set(permission.id, permission);
        });
      });

      return Array.from(permissions.values());
    });

    return {
      // Current user's roles
      currentUserRoles,

      // Current user's effective permissions
      currentUserPermissions,
      
      // Filtered permissions
      filteredPermissions: computed(() => {
        const filter = store.permissionFilter().toLowerCase();
        const permissions = store.permissions();
        
        if (!filter) {return permissions;}
        
        return permissions.filter(p => 
          p.name.toLowerCase().includes(filter) ||
          p.description.toLowerCase().includes(filter) ||
          p.resource.toLowerCase().includes(filter) ||
          p.category.toLowerCase().includes(filter)
        );
      }),
      
      // Filtered roles
      filteredRoles: computed(() => {
        const filter = store.roleFilter().toLowerCase();
        const roles = store.roles();
        
        if (!filter) {return roles;}
        
        return roles.filter(r => 
          r.name.toLowerCase().includes(filter) ||
          r.description.toLowerCase().includes(filter)
        );
      }),
      
      // Permission categories
      permissionCategories: computed(() => {
        const permissions = store.permissions();
        const categories = new Set(permissions.map(p => p.category));
        return Array.from(categories).sort();
      }),
      
      // Role hierarchy
      roleHierarchy: computed(() => {
        const roles = store.roles();
        const hierarchy = new Map<string, Role[]>();
        
        roles.forEach(role => {
          const children = roles.filter(r => r.parent_roles?.includes(role.id));
          hierarchy.set(role.id, children);
        });
        
        return hierarchy;
      }),
      
      // Permission statistics
      permissionStats: computed(() => ({
        totalPermissions: store.permissions().length,
        totalRoles: store.roles().length,
        userRoleAssignments: store.userRoles().length,
        systemRoles: store.roles().filter(r => r.is_system).length,
        customRoles: store.roles().filter(r => !r.is_system).length
      })),
      
      // Cache status
      cacheStatus: computed(() => {
        const expiry = store.cacheExpiry();
        const now = new Date();

        return {
          isValid: expiry ? now < expiry : false,
          expiresIn: expiry ? Math.max(0, expiry.getTime() - now.getTime()) : 0,
          size: store.permissionCache().size
        };
      }),

      // Error state computed - true if any load operation failed
      hasLoadError: computed(() =>
        store.permissionLoadError() !== null ||
        store.roleLoadError() !== null ||
        store.userRoleLoadError() !== null ||
        store.effectivePermissionsLoadError() !== null
      )
    };
  }),
  
  // Methods
  withMethods((store,
    permissionApi = inject(PermissionApiService),
    logger = inject(LoggerService),
    authStore = inject(AuthStore)
  ) => {
    // COLD-LOAD ERR_ABORTED-STORM FIX: coalesce refresh() bursts. Multiple
    // bootstrap/tenant-switch callers fire refresh() within the same tick; each
    // re-trigger cancels the prior switchMap-backed HTTP request (the observed
    // net::ERR_ABORTED storm). Skipping refreshes inside this window collapses a
    // burst to a single load while still allowing genuine later refreshes.
    const REFRESH_DEDUP_MS = 1000;
    let lastRefreshAt: number | null = null;

    const methods = {
      // Load all permissions — GAP-FE-026: emits Result<Permission[], PermissionLoadError>
      // through the inner pipeline so the success / failure branches are both
      // explicitly handled in the outer `tap`. Guards reading `permissionLoadError()`
      // see a typed failure, not a swallowed `EMPTY`.
      loadPermissions: rxMethod<void>((trigger$) =>
        trigger$.pipe(
          tap(() => {
            store.setPending();
            patchState(store, { permissionLoadError: null });
          }),
          switchMap(() =>
            permissionApi.getPermissions().pipe(
              map((permissions): PermissionResult<Permission[]> => ({
                ok: true,
                value: permissions ?? [],
              })),
              catchError((error): Observable<PermissionResult<Permission[]>> => {
                const loadError = classifyPermissionError(error);
                logger.error('Failed to load permissions:', { error, classified: loadError });
                return of({ ok: false, error: loadError });
              })
            )
          ),
          tap((result) => {
            if (result.ok) {
              patchState(store, { permissions: result.value, permissionLoadError: null });
              store.setFulfilled();
              logger.info('Loaded permissions', { count: result.value.length });
            } else {
              patchState(store, { permissionLoadError: result.error });
              store.setError(result.error.message);
            }
          })
        )
      ),

      // Load all roles — GAP-FE-026: same Result<Role[], PermissionLoadError> pattern.
      loadRoles: rxMethod<void>((trigger$) =>
        trigger$.pipe(
          tap(() => patchState(store, { roleLoadError: null })),
          switchMap(() =>
            permissionApi.getRoles().pipe(
              map((roles): PermissionResult<Role[]> => ({
                ok: true,
                value: roles ?? [],
              })),
              catchError((error): Observable<PermissionResult<Role[]>> => {
                const loadError = classifyPermissionError(error);
                logger.error('Failed to load roles:', { error, classified: loadError });
                return of({ ok: false, error: loadError });
              })
            )
          ),
          tap((result) => {
            if (result.ok) {
              patchState(store, { roles: result.value, roleLoadError: null });
              logger.info('Loaded roles', { count: result.value.length });
            } else {
              patchState(store, { roleLoadError: result.error });
              store.setError(result.error.message);
            }
          })
        )
      ),

      // Load user roles — GAP-FE-026: Result<UserRole[], PermissionLoadError>.
      loadUserRoles: rxMethod<string>((userId$) =>
        userId$.pipe(
          tap(() => patchState(store, { userRoleLoadError: null })),
          switchMap(userId =>
            permissionApi.getUserRoles(userId).pipe(
              map((userRoles): PermissionResult<UserRole[]> => ({
                ok: true,
                value: userRoles ?? [],
              })),
              catchError((error): Observable<PermissionResult<UserRole[]>> => {
                const loadError = classifyPermissionError(error);
                logger.error('Failed to load user roles:', { error, classified: loadError });
                return of({ ok: false, error: loadError });
              })
            )
          ),
          tap((result) => {
            if (result.ok) {
              patchState(store, { userRoles: result.value, userRoleLoadError: null });
              logger.info('Loaded user roles', { count: result.value.length });
            } else {
              patchState(store, { userRoleLoadError: result.error });
              store.setError(result.error.message);
            }
          })
        )
      ),

      // Get effective permissions for user — GAP-FE-026: Result<EffectivePermissions | null, PermissionLoadError>.
      // Null payload is preserved as a successful response so a backend explicitly
      // returning "no effective permissions" is distinguishable from a load failure.
      getEffectivePermissions: rxMethod<string>((userId$) =>
        userId$.pipe(
          tap(() => patchState(store, { effectivePermissionsLoadError: null })),
          switchMap(userId =>
            permissionApi.getEffectivePermissions(userId).pipe(
              map((effectivePermissions): PermissionResult<EffectivePermissions | null> => ({
                ok: true,
                value: effectivePermissions ?? null,
              })),
              catchError((error): Observable<PermissionResult<EffectivePermissions | null>> => {
                const loadError = classifyPermissionError(error);
                logger.error('Failed to get effective permissions:', { error, classified: loadError });
                return of({ ok: false, error: loadError });
              })
            )
          ),
          tap((result) => {
            if (result.ok) {
              if (result.value) {
                patchState(store, {
                  effectivePermissions: result.value,
                  cacheExpiry: new Date(result.value.cache_expires_at),
                  effectivePermissionsLoadError: null,
                });
              } else {
                // Successful response with null payload — clear stale state but
                // do NOT mark as a load error.
                patchState(store, { effectivePermissionsLoadError: null });
              }
            } else {
              patchState(store, { effectivePermissionsLoadError: result.error });
              store.setError(result.error.message);
            }
          })
        )
      ),
      
      // Check single permission
      // Security (DEV-1448): Uses deterministic sorted key generation to prevent
      // cache key collision attacks where an attacker could craft context objects
      // that produce the same cache key for different permission checks.
      // Performance (DEV-1449): Optimized to avoid O(n) cache recreation on each check.
      // Instead of creating a new Map on each cache miss, we directly mutate
      // the existing Map instance in the store, then trigger a shallow patchState
      // with the same reference to maintain reactive updates.
      hasPermission: () => (permission: string, context?: PermissionContext): boolean => {
        // Use deterministic sorted key generation to prevent collision attacks
        // Sorting ensures {a:1, b:2} and {b:2, a:1} produce identical cache keys
        const sortedContext = context
          ? Object.keys(context).sort().reduce((acc, key) => {
              acc[key] = context[key as keyof PermissionContext];
              return acc;
            }, {} as Record<string, unknown>)
          : {};
        // Encode permission name to prevent injection via special characters
        const cacheKey = `${encodeURIComponent(permission)}:${JSON.stringify(sortedContext)}`;

        // Get existing cache reference for O(1) lookup
        const existingCache = store.permissionCache();
        const cached = existingCache.get(cacheKey);

        if (cached !== undefined && store.cacheStatus().isValid) {
          return cached;
        }

        // Check against current user's effective permissions
        const userPermissions = store.currentUserPermissions();
        const hasAccess = userPermissions.some((p: Permission) =>
          p.name === permission ||
          (context?.resource !== undefined && context?.action !== undefined &&
            p.resource === context.resource && p.action === context.action)
        );

        // O(1) cache update: Mutate existing Map directly instead of O(n) recreation
        // This is safe because we're only adding entries, not modifying existing ones
        existingCache.set(cacheKey, hasAccess);
        // Trigger reactive update by patching with same reference
        // The signal system will detect the patchState call and notify subscribers
        patchState(store, { permissionCache: existingCache });

        return hasAccess;
      },
      
      // Check multiple permissions (AND logic)
      hasAllPermissions: () => (permissions: string[], context?: PermissionContext): boolean => permissions.every(permission => 
          methods.hasPermission()(permission, context)
        ),
      
      // Check multiple permissions (OR logic)
      hasAnyPermission: () => (permissions: string[], context?: PermissionContext): boolean => permissions.some(permission => 
          methods.hasPermission()(permission, context)
        ),
      
      // Check role membership (respects expires_at via currentUserRoles computed)
      hasRole: () => (roleId: string): boolean => {
        return store.currentUserRoles().some((ur: UserRole) => ur.role_id === roleId);
      },
      
      // Check any role membership (OR logic)
      hasAnyRole: () => (roleIds: string[]): boolean => roleIds.some(roleId => methods.hasRole()(roleId)),
      
      // Assign role to user — GAP-FE-026: Result<UserRole, PermissionLoadError>.
      assignRole: rxMethod<{userId: string, roleId: string}>((assignment$) =>
        assignment$.pipe(
          switchMap(({userId, roleId}) =>
            permissionApi.assignRole(userId, roleId).pipe(
              map((userRole): PermissionResult<UserRole> => ({ ok: true, value: userRole })),
              catchError((error): Observable<PermissionResult<UserRole>> => {
                const loadError = classifyPermissionError(error);
                logger.error('Failed to assign role:', { error, classified: loadError });
                return of({ ok: false, error: loadError });
              })
            )
          ),
          tap(result => {
            if (result.ok) {
              const currentRoles = [...store.userRoles()];
              currentRoles.push(result.value);
              patchState(store, { userRoles: currentRoles });
              methods.clearCache();
              logger.info('Role assigned successfully');
            } else {
              store.setError(result.error.message);
            }
          })
        )
      ),

      // Remove role from user — GAP-FE-026: Result<{userId, roleId}, PermissionLoadError>.
      removeRole: rxMethod<{userId: string, roleId: string}>((removal$) =>
        removal$.pipe(
          switchMap(({userId, roleId}) =>
            permissionApi.removeRole(userId, roleId).pipe(
              map((): PermissionResult<{userId: string, roleId: string}> => ({
                ok: true,
                value: { userId, roleId },
              })),
              catchError((error): Observable<PermissionResult<{userId: string, roleId: string}>> => {
                const loadError = classifyPermissionError(error);
                logger.error('Failed to remove role:', { error, classified: loadError });
                return of({ ok: false, error: loadError });
              })
            )
          ),
          tap(result => {
            if (result.ok) {
              const { userId, roleId } = result.value;
              const currentRoles = store.userRoles().filter((ur: UserRole) => !(ur.user_id === userId && ur.role_id === roleId));
              patchState(store, { userRoles: currentRoles });
              methods.clearCache();
              logger.info('Role removed successfully');
            } else {
              store.setError(result.error.message);
            }
          })
        )
      ),

      // Create new role — GAP-FE-026: Result<Role, PermissionLoadError>.
      createRole: rxMethod<Pick<Role, 'name' | 'description' | 'permissions'>>((roleData$) =>
        roleData$.pipe(
          switchMap(roleData =>
            permissionApi.createRole(roleData).pipe(
              map((newRole): PermissionResult<Role> => ({ ok: true, value: newRole })),
              catchError((error): Observable<PermissionResult<Role>> => {
                const loadError = classifyPermissionError(error);
                logger.error('Failed to create role:', { error, classified: loadError });
                return of({ ok: false, error: loadError });
              })
            )
          ),
          tap(result => {
            if (result.ok) {
              const currentRoles = [...store.roles()];
              currentRoles.push(result.value);
              patchState(store, { roles: currentRoles });
              logger.info('Role created successfully', { roleName: result.value.name });
            } else {
              store.setError(result.error.message);
            }
          })
        )
      ),

      // Update role — GAP-FE-026: Result<Role, PermissionLoadError>.
      updateRole: rxMethod<Role>((role$) =>
        role$.pipe(
          switchMap(role =>
            permissionApi.updateRole(role).pipe(
              map((updatedRole): PermissionResult<Role> => ({ ok: true, value: updatedRole })),
              catchError((error): Observable<PermissionResult<Role>> => {
                const loadError = classifyPermissionError(error);
                logger.error('Failed to update role:', { error, classified: loadError });
                return of({ ok: false, error: loadError });
              })
            )
          ),
          tap(result => {
            if (result.ok) {
              const updatedRole = result.value;
              const currentRoles = store.roles().map(r =>
                r.id === updatedRole.id ? updatedRole : r
              );
              patchState(store, { roles: currentRoles });
              methods.clearCache();
              logger.info('Role updated successfully', { roleName: updatedRole.name });
            } else {
              store.setError(result.error.message);
            }
          })
        )
      ),

      // Delete role — GAP-FE-026: Result<string (roleId), PermissionLoadError>.
      deleteRole: rxMethod<string>((roleId$) =>
        roleId$.pipe(
          switchMap(roleId =>
            permissionApi.deleteRole(roleId).pipe(
              map((): PermissionResult<string> => ({ ok: true, value: roleId })),
              catchError((error): Observable<PermissionResult<string>> => {
                const loadError = classifyPermissionError(error);
                logger.error('Failed to delete role:', { error, classified: loadError });
                return of({ ok: false, error: loadError });
              })
            )
          ),
          tap((result) => {
            if (result.ok) {
              const roleId = result.value;
              const currentRoles = store.roles().filter((r: Role) => r.id !== roleId);
              patchState(store, { roles: currentRoles });
              methods.clearCache();
              logger.info('Role deleted successfully');
            } else {
              store.setError(result.error.message);
            }
          })
        )
      ),
      
      // Set filters
      setPermissionFilter: (filter: string) => {
        patchState(store, { permissionFilter: filter });
      },
      
      setRoleFilter: (filter: string) => {
        patchState(store, { roleFilter: filter });
      },
      
      // Clear permission cache
      clearCache: () => {
        patchState(store, { 
          permissionCache: new Map(),
          cacheExpiry: null
        });
      },
      
      // Refresh all data.
      //
      // COLD-LOAD ERR_ABORTED-STORM FIX: refresh() is invoked from several
      // sources that all fire within the same cold-load tick — PermissionStore
      // onInit (loadPermissions + loadRoles), the principal-change effect
      // (loadUserRoles + getEffectivePermissions), the WebSocket debounced
      // refresh, and TenantStore.switchTenant() after a tenant switch. Each
      // underlying loader is an rxMethod backed by switchMap, so a fresh trigger
      // CANCELS the prior in-flight HTTP request -> the ~7 net::ERR_ABORTED
      // duplicates on /permissions + /roles observed on every cold load.
      //
      // Coalesce bursts: if a refresh() ran within REFRESH_DEDUP_MS, skip the
      // redundant re-trigger. The latest trigger still wins for genuinely new
      // data (interval > window), but a storm of near-simultaneous callers
      // collapses to a single load -> one network request per resource, no
      // abort churn, and no transient clearing of already-loaded data.
      refresh: () => {
        const now = Date.now();
        if (lastRefreshAt !== null && now - lastRefreshAt < REFRESH_DEDUP_MS) {
          logger.debug('PermissionStore.refresh() coalesced (within dedup window)', {
            sinceLastMs: now - lastRefreshAt,
          });
          return;
        }
        lastRefreshAt = now;

        methods.clearCache();
        methods.loadPermissions(undefined);
        methods.loadRoles(undefined);

        const currentUser = authStore.currentUser();
        if (currentUser) {
          methods.loadUserRoles(currentUser.id);
          methods.getEffectivePermissions(currentUser.id);
        }
      },
      
      // Check if store is ready
      isReady: () => store.status() === 'fulfilled' && store.permissions().length > 0,
      
      // Get permission by name
      getPermission: (name: string) => store.permissions().find(p => p.name === name),
      
      // Get role by ID
      getRole: (id: string) => store.roles().find(r => r.id === id)
    };
    
    return methods;
  }),
  
  // Lifecycle hooks
  withHooks((store) => {
    const logger = inject(LoggerService);
    const authStore = inject(AuthStore);
    return {
      onInit() {
        logger.debug('Enhanced PermissionStore initialized');

        // Load initial data
        store.loadPermissions(undefined);
        store.loadRoles(undefined);

        // GAP-FE-114: force construction of PermissionWebSocketService so backend
        // role-change events reach the FE. The service is providedIn: 'root' but
        // had no other production injection site, so Angular never instantiated
        // it; once constructed it owns its WS lifecycle (connect, reconnect,
        // debounced refresh) and pushes updates here via permissionStore.refresh().
        //
        // GAP-023 / NG0200 FIX: ngrx signalStore runs onInit() SYNCHRONOUSLY from
        // the SignalStore constructor (see @ngrx/signals SignalStore ctor), so
        // PermissionStore is still mid-resolution at this point. Because
        // PermissionWebSocketService injects PermissionStore back in a field
        // initializer, resolving it *now* re-enters PermissionStore and throws
        // NG0200 ("Circular dependency in DI detected"). The prior comment here
        // claimed the store was "fully constructed" by onInit — that is false for
        // ngrx signalStore and caused ADMIN-INTERFACE-77/7B/7C.
        //
        // Fix: capture the injector while still in injection context (cheap, no
        // cycle), then defer the back-edge to a microtask. By the time the
        // microtask runs, this constructor has returned and the provider is
        // cached, so the back-reference resolves cleanly. PermissionWebSocketService
        // is browser-only (opens a live WebSocket and uses setTimeout in its ctor),
        // so a microtask deferral is consistent with existing behavior and SSR-safe.
        const injector = inject(Injector);
        // Guard the deferred back-edge against a torn-down injector: in production
        // PermissionStore is providedIn:'root', so the injector outlives the
        // microtask, but under test teardown (TestBed resets the injector before
        // the microtask drains) runInInjectionContext would throw NG0205. Track
        // destruction via DestroyRef and skip the deferred wiring if it fired.
        let injectorDestroyed = false;
        inject(DestroyRef).onDestroy(() => {
          injectorDestroyed = true;
        });
        queueMicrotask(() => {
          if (injectorDestroyed) {
            return;
          }
          runInInjectionContext(injector, () => {
            const permissionWebSocket = inject(PermissionWebSocketService);
            // Mirror the WS connection status onto the store so computed UI badges
            // / banners reading store.websocketConnected track real connection
            // state. Created with an explicit { injector } because after the
            // microtask we are no longer in the implicit reactive injection
            // context; the effect is cleaned up when the root injector is destroyed.
            effect(() => {
              const status = permissionWebSocket.connectionStatus();
              patchState(store, { websocketConnected: status === 'connected' });
            }, { injector });
          });
        });

        // GAP-FE-008 R2 codex HIGH · the permission cache is keyed on
        // (permission, context) only. After logout / login as a
        // different user, cached `true` entries persisted and would
        // grant access to the new (lower-privileged) user until
        // cacheExpiry. Clear the cache whenever the authenticated
        // principal changes (id or tenant_id). Logout sets currentUser
        // to null; a subsequent login of any user triggers a re-fetch
        // because the cache is empty. We also re-load user roles for
        // the new principal so the store doesn't serve stale entries
        // while the next login completes.
        let lastUserKey: string | null = null;
        effect(() => {
          const u = authStore.currentUser();
          const key = u ? `${u.id}|${(u as { tenant_id?: string }).tenant_id ?? ''}` : null;
          if (key === lastUserKey) return;
          if (lastUserKey !== null) {
            logger.info('PermissionStore: auth principal changed, clearing permission cache', {
              previous: lastUserKey,
              current: key ?? '<signed-out>',
            });
            store.clearCache();
            if (u) {
              store.loadUserRoles(u.id);
              store.getEffectivePermissions(u.id);
            }
          }
          lastUserKey = key;
        });
      }
    };
  })
);
